import React from 'react';
import {
  ArrowLeft,
  Download,
  Loader2,
  Coins,
  RefreshCw,
  Box,
  Layers,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FloorplanState } from '@/types';
import { FloorplanStepIndicator } from '@/components/FloorplanStepIndicator';

interface FloorplanThreeDViewProps {
  floorplanState: FloorplanState;
  onGenerate3D: () => void;
  onNavigateToBranding: () => void;
  onDownload: (url: string, filename: string) => void;
  onBack: () => void;
  credits: number;
  floorplanUnits: number;
}

export const FloorplanThreeDView: React.FC<FloorplanThreeDViewProps> = ({
  floorplanState,
  onGenerate3D,
  onNavigateToBranding,
  onDownload,
  onBack,
  floorplanUnits,
}) => {
  const has3d = !!floorplanState.generated3dPath;
  // Pricing v4: the 3D view always follows an existing 2D in this flow, so the
  // Grundriss-Einheit is already consumed — 3D generations are free.
  const isGenerating = floorplanState.is3dGenerating;
  const generated3dUrl = floorplanState.generated3dUrl;
  const generated2dUrl = floorplanState.generated2dUrl;

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              3D Grundriss
            </h1>
            <p className="text-sm text-muted-foreground">
              Isometrische 3D-Darstellung
            </p>
          </div>
        </div>
        <Badge variant="outline" className="gap-1.5">
          <Coins className="w-3.5 h-3.5" />
          {floorplanUnits} Grundriss{floorplanUnits !== 1 ? 'e' : ''}
        </Badge>
      </div>

      <FloorplanStepIndicator currentStep="3d" />

      {/* Not Generated Yet */}
      {!isGenerating && !generated3dUrl && (
        <Card>
          <CardContent className="p-12 flex flex-col items-center justify-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Box className="w-10 h-10 text-primary" />
            </div>
            <div className="text-center max-w-md">
              <h2 className="text-xl font-semibold mb-2">
                3D-Dollhouse-Ansicht erstellen
              </h2>
              <p className="text-sm text-muted-foreground">
                Konvertieren Sie Ihren 2D-Grundriss in eine beeindruckende
                isometrische 3D-Darstellung mit Möbeln und Beleuchtung.
              </p>
            </div>

            {/* 2D Preview */}
            {generated2dUrl && (
              <div className="relative w-full max-w-sm rounded-lg overflow-hidden border bg-muted/30">
                <img
                  src={generated2dUrl}
                  alt="2D Grundriss"
                  className="w-full h-auto opacity-60"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Badge variant="secondary">Basis: 2D-Grundriss</Badge>
                </div>
              </div>
            )}

            <div className="flex flex-col items-center gap-3">
              <Button
                onClick={onGenerate3D}
                disabled={!generated2dUrl}
                className="gap-2"
                size="lg"
              >
                <Box className="w-5 h-5" />
                {has3d
                  ? '3D neu generieren (kostenlos)'
                  : '3D generieren (im Grundriss enthalten)'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Die 3D-Ansicht ist im Grundriss enthalten — keine zusätzlichen Kosten.
              </p>
              <Button variant="ghost" size="sm" onClick={onNavigateToBranding} className="gap-2 text-muted-foreground">
                Überspringen — direkt zum Branding
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generating State */}
      {isGenerating && !generated3dUrl && (
        <Card>
          <CardContent className="p-12 flex flex-col items-center justify-center gap-4">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">
              3D-Ansicht wird generiert…
            </p>
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {generated3dUrl && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">3D-Ansicht</h3>
              {isGenerating && (
                <Badge variant="secondary" className="gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Wird neu generiert…
                </Badge>
              )}
            </div>

            {/* 3D vs 2D comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {generated2dUrl && (
                <div className="space-y-2">
                  <Badge variant="secondary">2D Grundriss</Badge>
                  <div className="rounded-lg overflow-hidden border bg-muted/30">
                    <img
                      src={generated2dUrl}
                      alt="2D Grundriss"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Badge className="bg-emerald-600 hover:bg-emerald-700 gap-1">
                  <Box className="w-3 h-3" />
                  3D Dollhouse
                </Badge>
                <div className="rounded-lg overflow-hidden border bg-muted/30">
                  <img
                    src={generated3dUrl}
                    alt="Generierter 3D-Grundriss"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDownload(generated3dUrl, 'grundriss_3d.png')}
              >
                <Download className="w-4 h-4 mr-2" />
                3D herunterladen
              </Button>
              {generated2dUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDownload(generated2dUrl, 'grundriss_2d.png')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  2D herunterladen
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={onGenerate3D}
                disabled={isGenerating}
                title="Neu generieren ist kostenlos — im Grundriss enthalten"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Neu generieren (kostenlos)
              </Button>
              <Button size="sm" onClick={onNavigateToBranding} className="gap-2 ml-auto">
                <Layers className="w-4 h-4" />
                Weiter: Branding
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {floorplanState.generation3dError && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4 text-sm text-destructive">
            {floorplanState.generation3dError}
          </CardContent>
        </Card>
      )}

      {/* Back */}
      <div>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur 2D-Ansicht
        </Button>
      </div>
    </div>
  );
};
